import React from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  ListSubheader,
  IconButton,
  Divider,
} from '@mui/material';
import {
  FindCodesDialog,
} from 'codes';
import {
  listClaimsServicelines,
} from 'store/actions/claimsActions';
import { ServiceLineUtil } from 'utils';
import SettingsIcon from '@mui/icons-material/Settings';
import { withRouter } from 'utils';

const styles = (theme) => ({
  filterDropDown: {
    width: 168,
    marginRight: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
    },
  },
  // Opt-in via the `wide` prop: a multi-select summarises the chosen lines
  // ("Cardiology, Oncology"), which needs more room than a single name.
  filterDropDownWide: {
    width: 260,
    marginRight: theme.spacing(),
    [theme.breakpoints.down('md')]: {
      minWidth: '100%',
    },
  },
  parentServiceline: {
    borderTop: '1px solid #cccccc',
  },
  subServiceline: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  customServiceline: {
    fontSize: 14,
    paddingLeft: theme.spacing(4),
  },
  select: {
    '& .MuiMenuItem-root.Mui-selected, .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: '#00a887',
      color: 'white',
    },
  },
  settingsButton: {
    padding: 4,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
   customDivider: {
    margin: '8px 0',
    position: 'sticky',
    top: 36,
    backgroundColor: '#fff',
    zIndex: 1,
  },
  stickySelectedCount: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
    zIndex: 2,
  },
  stickyCustomHeader: {
    position: 'sticky',
    top: 48, // 36 (first header) + 17 (divider with margins)
    backgroundColor: '#fff',
    zIndex: 1,
  },
});

class ServiceLineSelect extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      open: false,
      findCodesOpen: false,
    };
  }

  componentDidMount() {
    const { servicelines, setFindServiceLine } = this.props;

    if (!servicelines) {
      this.fetchData();
    }

    if (setFindServiceLine) {
      setFindServiceLine(this.findServiceline);
    }
  }

  fetchData() {
    this.props.dispatch(listClaimsServicelines());
  }

  handleSettingsClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    // Close the dropdown first
    this.setState({ open: false }, () => {
      // Then navigate after a small delay to ensure dropdown closes
      setTimeout(() => {
        this.props.router.navigate('/custom_service_lines');
      }, 100);
    });
  };

  render() {
    const {
      classes,
      multiple,
      hideChildlessTopLevels,
      showSpecificServicelineIds,
      clientId,
      showCustomServiceLines,
      wide,
      isDiagnosis,
    } = this.props;

    const menuItems = [];
    if (this.props.servicelines && this.props.servicelines.length > 0) {
      let servicelines = this.props.servicelines;

      if (
        Array.isArray(showSpecificServicelineIds) &&
        showSpecificServicelineIds.length > 0
      ) {
        servicelines = servicelines.filter((serviceline) =>
          showSpecificServicelineIds.includes(serviceline.id)
        );
      }

      // Optional Dx/Px restriction (e.g. the Captured Codes tab, where the diagnosis
      // table only offers Dx lines and the procedure table only Px lines). Every line —
      // parent, child and custom — carries is_diagnosis, and children match their parent.
      if (typeof isDiagnosis === 'boolean') {
        const matchesType = (s) => !!s.is_diagnosis === isDiagnosis;
        servicelines = servicelines.filter(matchesType).map((s) => (
          s.children ? { ...s, children: s.children.filter(matchesType) } : s
        ));
      }

      // Separate custom and standard service lines
      // Filter custom service lines by client_id
      const customServiceLines = servicelines.filter(s => 
        s.client_id != null && s.client_id === clientId
      );
      const standardServiceLines = servicelines.filter(s => s.client_id == null);


      if (this.props.allowCodes) {
        let codesTypeLabel = 'By Codes';
        if (this.props.codesType === 'px') {
          codesTypeLabel = `By Px Codes (${this.props.codes.length})`;
        } else if (this.props.codesType === 'dx') {
          codesTypeLabel = `By Dx Codes (${this.props.codes.length})`;
        }
        menuItems.push(
          <MenuItem value='codes' key='codes'>
            <em>{codesTypeLabel}</em>
          </MenuItem>
        );
      }

      if (this.props.showAll) {
        menuItems.push(
          <MenuItem value='all' key='all'>
            <em>All</em>
          </MenuItem>
        );
      }

      // Add custom service lines section
      if (showCustomServiceLines) {
        // Add divider before custom section
        if (menuItems.length > 0) {
          menuItems.push(
            <Divider key="divider-before-custom" className={classes.customDivider} />
          );
        }

        menuItems.push(
          <ListSubheader 
            key="custom-header"
            style={{
              fontStyle: 'italic',
              fontWeight: 500,
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'default',
              pointerEvents: 'auto',
            }}
          >
            <span>CUSTOM SERVICE LINES</span>
            <IconButton
              size="small"
              className={classes.settingsButton}
              onMouseDown={this.handleSettingsClick}
              title="Manage Custom Service Lines"
              disableRipple
            >
              <SettingsIcon style={{ fontSize: 18 }} />
            </IconButton>
          </ListSubheader>
        );

        // Only show custom service lines if they exist
        if (customServiceLines.length > 0) {
          customServiceLines.forEach((s, index) => {
            if (!hideChildlessTopLevels || s.children?.length > 0) {
              menuItems.push(
                <MenuItem
                  value={s.id}
                  key={s.id}
                  className={classes.customServiceline}
                >
                  {s.name}
                </MenuItem>
              );
            }
          });
        } else {
          // Show a placeholder message when no custom service lines exist
          menuItems.push(
            <MenuItem 
              key="no-custom-lines" 
              disabled
              className={classes.customServiceline}
              style={{ 
                fontSize: 13, 
                fontStyle: 'italic',
                color: '#666'
              }}
            >
              No custom service lines yet
            </MenuItem>
          );
        }

      }

      // Add standard service lines
      standardServiceLines.forEach((s) => {
        if (!hideChildlessTopLevels || s.children?.length > 0) {
          menuItems.push(
            <MenuItem
              value={s.id}
              key={s.id}
              className={classes.parentServiceline}
            >
              {s.name}
            </MenuItem>
          );
        }
        if (this.props.showSubServicelines && s.children) {
          s.children.forEach((ss) => {
            menuItems.push(
              <MenuItem
                value={ss.id}
                key={ss.id}
                className={classes.subServiceline}
              >
                {ss.name}
              </MenuItem>
            );
          });
        }
      });
    }

    let selected = this.props.value;
    if (this.props.allowCodes && (this.props.codesType === 'dx' || this.props.codesType === 'px')) {
      selected = ['codes'];
    }
    return (
      <>
        <FormControl
          variant='outlined'
          className={wide ? classes.filterDropDownWide : classes.filterDropDown}
          key='filter-serviceline'
        >
          <InputLabel>Service Line</InputLabel>
          <Select
            value={selected || null}
            onChange={this.onServicelineChange}
            label='Service Line'
            multiple={multiple}
            renderValue={multiple ? this.renderSelectedValue : undefined}
            open={this.state.open}
            onOpen={this.onOpen}
            onClose={this.onClose}
            MenuProps={{
              PaperProps: {
                className: classes.select,
              }
            }}
          >
            {multiple && <ListSubheader 
              key="selected-count"
              className={classes.stickySelectedCount}
              style={{ 
                borderBottom: '1px solid black', 
                fontSize: 14, 
                fontWeight: 500,
                color: '#000',
              }}
            >
              {[].concat(selected ?? []).filter((id) => id !== 'all').length}{' '}
              selected
            </ListSubheader>}
            {menuItems}
          </Select>
        </FormControl>
        {this.state.findCodesOpen && (
          <FindCodesDialog
            open={this.state.findCodesOpen}
            onClose={this.onCodesChange}
            codes={this.props.codes}
            codesType={this.props.codesType}
          />
        )}
      </>
    );
  }

  findServiceline(id) {
    return ServiceLineUtil.findServiceLine(this.props.servicelines, id);
  }

  // MUI's default renderer for a multi-select prints the raw values, which for
  // service lines means a row of ids ("12, 34"). Render names instead, and
  // collapse long selections to a count so the field doesn't overflow.
  renderSelectedValue = (selected) => {
    const ids = [].concat(selected ?? []);
    if (ids.length === 0) {
      return <em>All</em>;
    }
    if (ids.includes('all')) {
      return <em>All</em>;
    }
    if (ids.includes('codes')) {
      return <em>By Codes</em>;
    }
    if (ids.length > 2) {
      return `${ids.length} service lines`;
    }
    return ids.map((id) => this.servicelineName(id)).join(', ');
  };

  // findServiceline compares ids with ===; values can arrive as strings (query
  // params, build-time defaults), so fall back to a loose match and then to the
  // raw value rather than rendering an empty field.
  servicelineName = (id) => {
    let match = this.findServiceline(id);
    if (!match) {
      const flat = (this.props.servicelines || []).flatMap((s) => [
        s,
        ...(s.children || []),
      ]);
      match = flat.find((s) => String(s.id) === String(id));
    }
    return match?.name ?? String(id);
  };

  onOpen = () => {
    this.setState({
      open: true,
    });
  };

  onClose = () => {
    this.setState({
      open: false,
    });
  };

onServicelineChange = (event) => {
  const { multiple, value, showAll } = this.props;

  if (multiple) {
    let ids = event.target.value;
    if (ids.length > 0) {
      if (ids.includes('all')) {
        if (ids[ids.length - 1] === 'all') {
          ids = ['all'];
        } else {
          ids = ids.filter((id) => id !== 'all');
        }
      } else if (ids.includes('codes')) {
        if (ids[ids.length - 1] === 'codes') {
          ids = ['codes'];
          return this.setState({ findCodesOpen: true });
        } else {
          ids = ids.filter((id) => id !== 'codes');
        }
      } else {
        const newValue = event.target.value.find(
          (selectedValue) => !value.includes(selectedValue)
        );

        if (newValue) {
          const { servicelines, clientId } = this.props;

          // Get all custom service line IDs
          const customServiceLineIds = servicelines
            .filter((s) => s.client_id != null && s.client_id === clientId)
            .map((s) => s.id);

          const isNewValueCustom = customServiceLineIds.includes(newValue);

          if (isNewValueCustom) {
            // If selecting a custom service line, ONLY keep that one (remove ALL others)
            ids = [newValue];
            this.props.onChange(ids, 'servicelines');
            this.setState({ open: false }); // Close dropdown
            return; // Early return
          } else {
            // If selecting a standard service line, remove all custom service lines
            ids = ids.filter((id) => !customServiceLineIds.includes(id));

            // Then apply original parent/child logic for standard service lines
            const serviceline = this.findServiceline(newValue);
            if (serviceline.parent_id) {
              ids = ids.filter((id) => id !== serviceline.parent_id);
            } else {
              ids = ids.filter(
                (id) =>
                  !serviceline.children?.find(
                    (child) => child.id === id
                  )
              );
            }
          }
        }
      }
      this.props.onChange(ids, 'servicelines');
    } else {
      if (this.props.codesType === 'dx' || this.props.codesType === 'px') {
        return this.setState({ findCodesOpen: true });
      }
      // Deselecting the last item used to be swallowed here, leaving the field
      // showing a selection the parent no longer holds. Fall back to "All".
      if (showAll) {
        this.props.onChange(['all'], 'servicelines');
      }
    }
  } else {
    const id = event.target.value;
    const serviceline = this.findServiceline(id);
    if (serviceline) {
      this.props.onChange(serviceline.id, serviceline.parent_id);
    } else {
      this.props.onChange(id);
    }
  }
};

  onCodesChange = (codesType, codes) => {
    if (codes && codes.length > 0) {
      this.props.onChange(codes, codesType);
      this.setState({ open: false });
    } else {
      // cancel back to previous state
    }
    // close dialog
    this.setState({
      findCodesOpen: false,
      open: false,
    })
  };
}

ServiceLineSelect.propTypes = {
  classes: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  showAll: PropTypes.bool,
  showSubServicelines: PropTypes.bool,
  hideChildlessTopLevels: PropTypes.bool,
  multiple: PropTypes.bool,
  allowCodes: PropTypes.bool,
  codes: PropTypes.array,
  codesType: PropTypes.string,
  servicelines: PropTypes.array,
  setFindServiceLine: PropTypes.func,
  showSpecificServicelineIds: PropTypes.array,
  wide: PropTypes.bool,
  isDiagnosis: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  router: PropTypes.object.isRequired,
  clientId: PropTypes.number,
};

function mapStateToProps(state) {
  return {
    servicelines: state.claims && state.claims.servicelines_grouped,
    clientId: state.app && state.app.selectedClient,
  };
}

const styled = withStyles(styles)(ServiceLineSelect);
const connected = connect(mapStateToProps)(styled);
const routed = withRouter(connected);
export { routed as ServiceLineSelect };